import React, { lazy } from "react";
import { useTranslation } from "react-i18next";

// Set while a page reload, triggered by a failed chunk load, is in progress
const RELOAD_FLAG = "chunk_reload_attempted";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readFlag = () => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch (error) {
    return true; // Storage unavailable: never auto-reload, so a reload loop is impossible
  }
};
const writeFlag = (value) => {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, "1");
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch (error) {
    // Ignore: see readFlag
  }
};

// React.lazy for route pages. A failed chunk download (flaky network, or a new deploy that
// replaced the old chunk files) is retried once; if it still fails, the page reloads once to
// pick up the current build. After that the error reaches RouteErrorBoundary.
// The returned component has preload(), which downloads its chunk in the background.
export function lazyWithRetry(factory) {
  const Component = lazy(async () => {
    try {
      const module = await factory();
      writeFlag(false);
      return module;
    } catch (firstError) {
      await wait(1000);
      try {
        const module = await factory();
        writeFlag(false);
        return module;
      } catch (error) {
        if (!readFlag()) {
          writeFlag(true);
          window.location.reload();
          return new Promise(() => {}); // Keep the Suspense fallback up until the reload happens
        }
        throw error;
      }
    }
  });
  // Webpack caches a loaded chunk, so a later render of the page doesn't download it again.
  // A failed preload is ignored (no retry or reload): webpack retries it when the page opens.
  Component.preload = () => factory().catch(() => {});
  return Component;
}

function LoadError() {
  const { t } = useTranslation();
  return (
    <div className="Empty_data" role="alert">
      <span>{t("common.pageLoadError")}</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ marginTop: "12px", padding: "8px 20px", borderRadius: "8px", border: "1px solid #007bff", background: "#007bff", color: "#fff", fontWeight: 600 }}
      >
        {t("common.retry")}
      </button>
    </div>
  );
}

// Shows a retry message instead of a blank screen when a route page can't load.
// A change of resetKey (the pathname) clears the error, without remounting healthy pages.
export class RouteErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Page failed to load:", error);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? <LoadError /> : this.props.children;
  }
}
